import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CircleMinus, Merge, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import {
  CheckboxField,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuTrigger,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip
} from '@/components/ui/menu';
import { DayInput, Select } from '@/components/ui/select';
import { Td, Th } from '@/components/ui/table';
import {
  useActivities,
  useBillables,
  useCreateActivity,
  useCreditFeeTemplates
} from '@/hooks/useReference';
import { useAddBillingParty } from '@/features/manage/BillingPartyDialog';
import { useAddTemplate } from '@/features/credits/TemplateDialog';
import { useSaveInvoice } from '@/hooks/useInvoices';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { fetchCreditsAndFees } from '@/api/misc';
import { qk } from '@/lib/query';
import { formatDay, money, hoursShort, today } from '@/lib/format';
import { tempId } from '@/lib/utils';
import type { CreditFeeTemplate, CreditOrFee, InvoiceDetail, InvoiceEntry } from '@/types';

/**
 * A staged editor: everything is edited locally and committed with one Save.
 *
 * v1 mixed the two models — field edits were staged but adding/removing an
 * entry fired immediately, so "Discard changes" still left the entry set
 * permanently mutated. Here nothing hits the network until Save, and Save is a
 * single optimistic write: the preview behind the dialog updates the instant
 * you close it.
 */
export function InvoiceEditor({
  open,
  onOpenChange,
  invoice
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: InvoiceDetail;
}) {
  const activities = useActivities();
  const billables = useBillables();
  const templates = useCreditFeeTemplates();
  const createActivity = useCreateActivity();
  const addParty = useAddBillingParty((party) => patchDraft({ billTo: party }));
  const addFromTemplate = (template: CreditFeeTemplate) =>
    setDraft((d) => ({
      ...d,
      creditsAndFees: [
        ...d.creditsAndFees,
        {
          id: tempId('cf'),
          clientId: d.clientId,
          clientName: d.clientName,
          invoiceId: d.id,
          kind: template.kind,
          description: template.name,
          amount: template.amount,
          isHourly: template.isHourly,
          date: today(),
          applied: true
        }
      ]
    }));
  const addTemplate = useAddTemplate(addFromTemplate);
  const save = useSaveInvoice();

  // The client's credits/fees no invoice holds yet (a retainer recorded on the
  // Credits & fees page after this draft was made). Picking one attaches the
  // existing row on Save rather than copying it.
  const openCreditsFees = useQuery({
    queryKey: [...qk.creditsAndFeesRoot, 'open', invoice.clientId] as const,
    queryFn: ({ signal }) =>
      fetchCreditsAndFees({ clientId: invoice.clientId, invoiced: false, sortOrder: 'asc' }, signal),
    enabled: open,
    staleTime: 0
  });

  const [draft, setDraft] = useState<InvoiceDetail>(invoice);
  const [tab, setTab] = useState('details');
  // Entries folded into another line. Unlike a removed line, they stay billed.
  const [mergedIds, setMergedIds] = useState<string[]>([]);
  // Entries to delete outright on Save, rather than return to the timesheet.
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [pendingLine, setPendingLine] = useState<{
    action: 'drop' | 'delete';
    entry: InvoiceEntry;
  } | null>(null);
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [skipDropConfirm, setSkipDropConfirm] = useLocalStorage(
    'billable.skipDropLineConfirm',
    false
  );
  const [skipDeleteConfirm, setSkipDeleteConfirm] = useLocalStorage(
    'billable.skipDeleteLineConfirm',
    false
  );

  // Re-seed whenever a different invoice (or a fresh open) comes in.
  useEffect(() => {
    if (open) {
      setDraft(invoice);
      setMergedIds([]);
      setDeletedIds([]);
      setPendingLine(null);
    }
  }, [open, invoice]);

  const totals = useMemo(() => {
    const subtotal = draft.entries.reduce(
      (sum, e) => sum + e.hours * (e.hourlyRate ?? draft.hourlyRate),
      0
    );
    const fees = draft.creditsAndFees
      .filter((c) => c.kind === 'fee')
      .reduce((s, c) => s + c.amount, 0);
    const credits = draft.creditsAndFees
      .filter((c) => c.kind === 'credit')
      .reduce((s, c) => s + c.amount, 0);
    return {
      subtotal,
      fees,
      credits,
      total: subtotal + fees - credits,
      hours: draft.entries.reduce((s, e) => s + e.hours, 0)
    };
  }, [draft]);

  const patchDraft = (patch: Partial<InvoiceDetail>) => setDraft((d) => ({ ...d, ...patch }));

  const patchEntry = (id: string, patch: Partial<InvoiceEntry>) =>
    setDraft((d) => ({
      ...d,
      entries: d.entries.map((e) => (e.id === id ? { ...e, ...patch } : e))
    }));

  const removeEntry = (id: string) =>
    setDraft((d) => ({ ...d, entries: d.entries.filter((e) => e.id !== id) }));

  const applyLineAction = (action: 'drop' | 'delete', id: string) => {
    removeEntry(id);
    if (action === 'delete') setDeletedIds((ids) => [...ids, id]);
  };

  const requestLineAction = (action: 'drop' | 'delete', entry: InvoiceEntry) => {
    if (action === 'drop' ? skipDropConfirm : skipDeleteConfirm) {
      applyLineAction(action, entry.id);
      return;
    }
    setDontAskAgain(false);
    setPendingLine({ action, entry });
  };

  const confirmLineAction = () => {
    if (!pendingLine) return;
    applyLineAction(pendingLine.action, pendingLine.entry.id);
    if (dontAskAgain) {
      if (pendingLine.action === 'drop') setSkipDropConfirm(true);
      else setSkipDeleteConfirm(true);
    }
  };

  /** Add `sourceId`'s hours to `targetId` and drop the source line. */
  const mergeEntry = (sourceId: string, targetId: string) => {
    setDraft((d) => {
      const source = d.entries.find((e) => e.id === sourceId);
      if (!source) return d;
      return {
        ...d,
        entries: d.entries
          .filter((e) => e.id !== sourceId)
          .map((e) =>
            e.id === targetId
              ? { ...e, hours: Math.round((e.hours + source.hours) * 100) / 100 }
              : e
          )
      };
    });
    setMergedIds((ids) => [...ids, sourceId]);
  };

  const commit = () => {
    const next: InvoiceDetail = {
      ...draft,
      subtotal: totals.subtotal,
      feesTotal: totals.fees,
      creditsTotal: totals.credits,
      totalAmount: totals.total,
      totalHours: totals.hours
    };
    save.mutate({
      id: invoice.id,
      next,
      patch: {
        invoiceNumber: draft.invoiceNumber,
        issueDate: draft.issueDate ?? undefined,
        dueDate: draft.dueDate ?? undefined,
        hourlyRate: draft.hourlyRate,
        notes: draft.notes,
        billableId: draft.billTo?.id ?? null,
        billableName: draft.billTo?.name,
        billableAddress: draft.billTo?.address,
        billablePhone: draft.billTo?.phone,
        billableEmails: draft.billTo?.emails,
        entries: draft.entries.map((e) => ({
          id: e.id,
          activityId: e.activityId,
          date: e.date ?? undefined,
          hours: e.hours,
          hourlyRate: e.hourlyRate
        })),
        // The server only updates the rows it is sent, so removals have to be
        // named. Without this they came back on the refetch after Save.
        removedEntryIds: invoice.entries
          .filter((e) => !draft.entries.some((d) => d.id === e.id))
          .filter((e) => !mergedIds.includes(e.id) && !deletedIds.includes(e.id))
          .map((e) => e.id),
        mergedEntryIds: mergedIds,
        deletedEntryIds: deletedIds,
        creditsAndFees: draft.creditsAndFees
      }
    });
    onOpenChange(false);
  };

  const warnSent = invoice.status !== 'draft' && invoice.status !== 'tentative';

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="2xl"
      title={`Edit invoice ${invoice.invoiceNumber}`}
      description="Nothing is saved until you hit Save."
      footer={
        <>
          <div className="mr-auto text-sm text-muted">
            <span className="tnum font-medium text-[var(--text)]">{money(totals.total)}</span>{' '}
            · {hoursShort(totals.hours)} hours
          </div>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={commit}>
            Save
          </Button>
        </>
      }
    >
      {warnSent ? (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          This invoice has already left draft. Edits here won’t change any copy the recipient
          already has.
        </div>
      ) : null}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="entries">Line items ({draft.entries.length})</TabsTrigger>
          <TabsTrigger value="credits">
            Credits &amp; fees ({draft.creditsAndFees.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Invoice number">
              <Input
                className="tnum"
                value={draft.invoiceNumber}
                onChange={(e) => patchDraft({ invoiceNumber: e.target.value })}
              />
            </Field>
            <Field label="Hourly rate" hint="Applies to any line item without its own rate.">
              <NumberInput
                value={draft.hourlyRate}
                onChange={(v) => patchDraft({ hourlyRate: v ?? 0 })}
              />
            </Field>
            <Field label="Issue date">
              <DayInput
                className="w-full"
                value={draft.issueDate ?? ''}
                onChange={(e) => patchDraft({ issueDate: e.target.value })}
              />
            </Field>
            <Field label="Due date">
              <DayInput
                className="w-full"
                value={draft.dueDate ?? ''}
                onChange={(e) => patchDraft({ dueDate: e.target.value })}
              />
            </Field>
          </div>

          <Field label="Billing party">
            <Combobox
              options={(billables.data ?? []).map((b) => ({
                value: b.id,
                label: b.name,
                hint: b.organization ?? undefined
              }))}
              loading={billables.isPending}
              value={draft.billTo?.id ?? null}
              onChange={(id) =>
                patchDraft({ billTo: billables.data?.find((b) => b.id === id) ?? null })
              }
              onAddNew={addParty.open}
              createLabel="Add billing party"
              placeholder="Who receives this invoice?"
            />
          </Field>
          {addParty.dialog}

          {draft.billTo ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Email(s)" hint="Comma separated.">
                <Input
                  value={draft.billTo.emails.join(', ')}
                  onChange={(e) =>
                    patchDraft({
                      billTo: draft.billTo
                        ? {
                            ...draft.billTo,
                            emails: e.target.value
                              .split(',')
                              .map((s) => s.trim())
                              .filter(Boolean)
                          }
                        : null
                    })
                  }
                />
              </Field>
              <Field label="Phone">
                <Input
                  value={draft.billTo.phone ?? ''}
                  onChange={(e) =>
                    patchDraft({
                      billTo: draft.billTo ? { ...draft.billTo, phone: e.target.value } : null
                    })
                  }
                />
              </Field>
              <Field label="Address" className="sm:col-span-2">
                <Textarea
                  value={draft.billTo.address ?? ''}
                  onChange={(e) =>
                    patchDraft({
                      billTo: draft.billTo ? { ...draft.billTo, address: e.target.value } : null
                    })
                  }
                />
              </Field>
            </div>
          ) : null}

          <Field label="Notes" hint="Printed at the bottom of the invoice.">
            <Textarea
              value={draft.notes ?? ''}
              onChange={(e) => patchDraft({ notes: e.target.value })}
              placeholder="Payment instructions, thanks, reference numbers…"
            />
          </Field>
        </TabsContent>

        <TabsContent value="entries">
          {/*
            Fixed layout, each column as wide as its control needs, scrolling
            sideways when the dialog is narrower than that. Auto layout squeezed the number inputs first
            (their only minimum is zero), so a narrow window showed "12" for
            12.75 hours.
          */}
          <div className="scroll-thin max-h-[26rem] overflow-auto rounded-xl border border-[var(--line)]">
            <table className="w-full min-w-[56rem] table-fixed border-collapse text-sm">
              <thead className="sticky top-0 z-10">
                <tr>
                  {/* Activity takes what is left; the 56rem floor keeps it at 14rem or more. */}
                  <Th>Activity</Th>
                  <Th width="12rem">Date</Th>
                  <Th align="right" width="7rem">
                    Hours
                  </Th>
                  <Th align="right" width="7rem">
                    Rate
                  </Th>
                  <Th align="right" width="8rem">
                    Amount
                  </Th>
                  <Th width="8rem" />
                </tr>
              </thead>
              <tbody>
                {draft.entries.map((entry) => (
                  <tr key={entry.id} className="border-b border-[var(--line)] last:border-0">
                    <Td>
                      <Combobox
                        size="sm"
                        className="w-full"
                        options={(activities.data ?? []).map((a) => ({
                          value: a.id,
                          label: a.name
                        }))}
                        value={entry.activityId}
                        onChange={(v) =>
                          patchEntry(entry.id, {
                            activityId: v,
                            activityName:
                              activities.data?.find((a) => a.id === v)?.name ?? null
                          })
                        }
                        onCreate={async (name) => {
                          const created = await createActivity.mutateAsync({ name });
                          patchEntry(entry.id, { activityId: created.id, activityName: created.name });
                        }}
                        createLabel="Add activity"
                      />
                    </Td>
                    <Td>
                      <DayInput
                        size="sm"
                        className="w-full"
                        value={entry.date ?? ''}
                        onChange={(e) => patchEntry(entry.id, { date: e.target.value })}
                      />
                    </Td>
                    <Td>
                      <NumberInput
                        size="sm"
                        aria-label="Hours"
                        parse="duration"
                        value={entry.hours}
                        onChange={(v) => patchEntry(entry.id, { hours: v ?? 0 })}
                      />
                    </Td>
                    <Td>
                      <NumberInput
                        size="sm"
                        aria-label="Rate"
                        allowEmpty
                        placeholder={String(draft.hourlyRate)}
                        value={entry.hourlyRate}
                        onChange={(v) => patchEntry(entry.id, { hourlyRate: v })}
                      />
                    </Td>
                    <Td align="right" className="tnum">
                      {money(entry.hours * (entry.hourlyRate ?? draft.hourlyRate))}
                    </Td>
                    <Td align="right" className="whitespace-nowrap">
                      {draft.entries.length > 1 ? (
                        <Menu>
                          <Tooltip content="Merge into another line">
                            <MenuTrigger asChild>
                              <Button variant="ghost" size="iconSm" aria-label="Merge into another line">
                                <Merge />
                              </Button>
                            </MenuTrigger>
                          </Tooltip>
                          <MenuContent className="max-h-72 overflow-y-auto">
                            <MenuLabel>Merge {hoursShort(entry.hours)} h into…</MenuLabel>
                            {draft.entries
                              .filter((e) => e.id !== entry.id)
                              .map((target) => (
                                <MenuItem
                                  key={target.id}
                                  onSelect={() => mergeEntry(entry.id, target.id)}
                                >
                                  <span className="min-w-0 flex-1 truncate">
                                    {target.activityName ?? 'Line item'}
                                  </span>
                                  <span className="tnum shrink-0 text-sm text-muted">
                                    {formatDay(target.date)} · {hoursShort(target.hours)} h
                                  </span>
                                </MenuItem>
                              ))}
                          </MenuContent>
                        </Menu>
                      ) : null}
                      <Tooltip content="Drop from invoice">
                        <Button
                          variant="ghost"
                          size="iconSm"
                          aria-label="Drop from invoice"
                          onClick={() => requestLineAction('drop', entry)}
                        >
                          <CircleMinus />
                        </Button>
                      </Tooltip>
                      <Tooltip content="Delete time entry">
                        <Button
                          variant="ghost"
                          size="iconSm"
                          aria-label="Delete time entry"
                          className="hover:text-red-600"
                          onClick={() => requestLineAction('delete', entry)}
                        >
                          <Trash2 />
                        </Button>
                      </Tooltip>
                    </Td>
                  </tr>
                ))}
                {draft.entries.length === 0 ? (
                  <tr>
                    <Td colSpan={6} className="py-10 text-center text-sm text-muted">
                      No line items. Saving now leaves an empty invoice.
                    </Td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-sm text-muted">
            Merge adds a line's hours to another line and keeps it billed. Drop puts the
            time back on the timesheet as unbilled. Delete removes it from the timesheet too.
            {skipDropConfirm || skipDeleteConfirm ? (
              <>
                {' '}
                <button
                  type="button"
                  className="underline underline-offset-2 hover:text-[var(--text)]"
                  onClick={() => {
                    setSkipDropConfirm(false);
                    setSkipDeleteConfirm(false);
                  }}
                >
                  Turn confirmations back on
                </button>
              </>
            ) : null}
          </p>
          <ConfirmDialog
            open={Boolean(pendingLine)}
            onOpenChange={(v) => !v && setPendingLine(null)}
            title={pendingLine?.action === 'delete' ? 'Delete this time entry?' : 'Drop this line?'}
            description={
              pendingLine
                ? `${pendingLine.entry.activityName ?? 'Line item'}, ${formatDay(pendingLine.entry.date)}, ${hoursShort(pendingLine.entry.hours)} h. ${
                    pendingLine.action === 'delete'
                      ? 'It leaves this invoice and the timesheet for good when you save.'
                      : 'It goes back on the timesheet as unbilled when you save.'
                  }`
                : ''
            }
            confirmLabel={pendingLine?.action === 'delete' ? 'Delete' : 'Drop'}
            destructive={pendingLine?.action === 'delete'}
            onConfirm={confirmLineAction}
            extra={
              <CheckboxField
                checked={dontAskAgain}
                onCheckedChange={setDontAskAgain}
                label="Don’t ask again"
              />
            }
          />
        </TabsContent>

        <TabsContent value="credits" className="space-y-3">
          {draft.creditsAndFees.map((item) => (
            <CreditFeeRow
              key={item.id}
              item={item}
              onChange={(patch) =>
                setDraft((d) => ({
                  ...d,
                  creditsAndFees: d.creditsAndFees.map((c) =>
                    c.id === item.id ? { ...c, ...patch } : c
                  )
                }))
              }
              onRemove={() =>
                setDraft((d) => ({
                  ...d,
                  creditsAndFees: d.creditsAndFees.filter((c) => c.id !== item.id)
                }))
              }
            />
          ))}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={() =>
                setDraft((d) => ({
                  ...d,
                  creditsAndFees: [
                    ...d.creditsAndFees,
                    {
                      id: tempId('cf'),
                      clientId: d.clientId,
                      clientName: d.clientName,
                      invoiceId: d.id,
                      kind: 'fee',
                      description: '',
                      amount: 0,
                      isHourly: false,
                      date: today(),
                      applied: true
                    }
                  ]
                }))
              }
            >
              <Plus />
              Add line
            </Button>

            {(() => {
              const pickable = (openCreditsFees.data?.items ?? []).filter(
                (f) => !draft.creditsAndFees.some((c) => c.id === f.id)
              );
              return pickable.length ? (
                <Combobox
                  className="w-64"
                  options={pickable.map((f) => ({
                    value: f.id,
                    label: f.description,
                    hint: money(f.kind === 'credit' ? -f.amount : f.amount)
                  }))}
                  value={null}
                  placeholder={`Add unbilled (${pickable.length})`}
                  onChange={(id) => {
                    const item = pickable.find((f) => f.id === id);
                    if (!item) return;
                    setDraft((d) => ({
                      ...d,
                      creditsAndFees: [
                        ...d.creditsAndFees,
                        { ...item, invoiceId: d.id, applied: true }
                      ]
                    }));
                  }}
                />
              ) : null;
            })()}

            <Combobox
              className="w-56"
              options={(templates.data ?? []).map((t) => ({
                value: t.id,
                label: t.name,
                hint: money(t.kind === 'credit' ? -t.amount : t.amount)
              }))}
              loading={templates.isPending}
              value={null}
              placeholder="Add from a template"
              onChange={(id) => {
                const template = templates.data?.find((t) => t.id === id);
                if (template) addFromTemplate(template);
              }}
              onAddNew={addTemplate.open}
              createLabel="Add template"
            />
            {addTemplate.dialog}
          </div>
        </TabsContent>
      </Tabs>
    </Dialog>
  );
}

function CreditFeeRow({
  item,
  onChange,
  onRemove
}: {
  item: CreditOrFee;
  onChange: (patch: Partial<CreditOrFee>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-xl border border-[var(--line)] p-3">
      <Field label="Type" className="w-28">
        <Select
          value={item.kind}
          onChange={(v) => onChange({ kind: v as CreditOrFee['kind'] })}
          options={[
            { value: 'fee', label: 'Fee' },
            { value: 'credit', label: 'Credit' }
          ]}
          className="w-full"
        />
      </Field>
      <Field label="Description" className="min-w-40 flex-1">
        <Input
          value={item.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Filing fee, courier, discount…"
        />
      </Field>
      <Field label="Amount" className="w-28">
        <NumberInput value={item.amount} onChange={(v) => onChange({ amount: v ?? 0 })} />
      </Field>
      <Field label="Date" className="w-40">
        <DayInput
          className="w-full"
          value={item.date ?? ''}
          onChange={(e) => onChange({ date: e.target.value })}
        />
      </Field>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Remove"
        className="mb-0.5 hover:text-red-600"
        onClick={onRemove}
      >
        <Trash2 />
      </Button>
    </div>
  );
}
